/**
 * createProgramme.mock.ts — mock content for the VER-128 Create Programme preview.
 *
 * Every string here comes from the "Verbal Prepare Refresh" design canvas. It is copy for
 * review, not data: nothing in this file is fetched, and nothing it describes exists in the
 * database. The collection below is deliberately the canvas's placeholder collection
 * ("A Real Durwan - update", with "...." where the designer left copy unwritten) rather than a
 * real seeded collection — a reviewer must be able to tell at a glance that the screen is not
 * reading live content.
 *
 * TODO [VER-128 follow-ups]: replace with real reads once the fields exist —
 *   populations  → new PopulationGroup enum + Programme.populationGroup
 *   collection   → StoryCollection (+ sectoral application, curriculum keywords)
 *   stories      → MappedStory (+ discussion theme, learning objective, learning transfer)
 */

import {
    CollectionOverview,
    DeliveryFormatOption,
    NextActionOption,
    PopulationOption,
    ProgrammeDraft,
} from "./createProgramme.types";

/** Step 1 — six population groups, in the canvas's reading order. */
export const POPULATION_OPTIONS: PopulationOption[] = [
    {
        key: "child",
        title: "Child",
        subtitle: "Aged 4–10",
        body: "Community, school or care setting. Picture-led assessment, simplified language.",
        summary: "Child · 4–10 selected",
    },
    {
        key: "young-person",
        title: "Young person",
        subtitle: "Aged 11–16",
        body: "Youth justice, school, community. Adapted language and engagement mechanics.",
        summary: "Young person · 11–16 selected",
    },
    {
        key: "justice",
        title: "Prison / Probation",
        subtitle: "Criminal justice",
        body: "Institutional setting protocols. Trauma-informed, resistance-aware assessment.",
        summary: "Prison / Probation selected",
    },
    {
        key: "older-adult",
        title: "Older adult",
        subtitle: "Social isolation",
        body: "Community or residential. Life narrative, connection and belonging themes.",
        summary: "Older adult selected",
    },
    {
        key: "dementia",
        title: "Dementia / Cognitive",
        subtitle: "Care home or specialist",
        body: "Adapted pacing, sensory anchors, caregiver co-delivery model.",
        summary: "Dementia / Cognitive selected",
    },
    {
        key: "teacher-cpd",
        title: "School / Teacher CPD",
        subtitle: "Classroom or staff dev",
        body: "Curriculum-linked. Whole-class, small group or staff development formats.",
        summary: "School / Teacher CPD selected",
    },
];

/** Step 2 — three delivery formats. */
export const DELIVERY_FORMATS: DeliveryFormatOption[] = [
    {
        key: "one-to-one",
        title: "1-2-1 Session",
        body: "One practitioner, one participant. Full individual assessment, personal story collection and individual dashboard.",
        tag: "Full assessment pathway",
        tagTone: "success",
        summary: "1-2-1 Session selected",
    },
    {
        key: "group",
        title: "Group Session",
        body: "Working with 2–20 participants together. Anonymous group check-in, collective story collection and group dashboard.",
        tag: "2–20 participants",
        tagTone: "default",
        summary: "Group Session selected",
    },
    {
        key: "classroom",
        title: "School Classroom",
        body: "Whole-class delivery. Named students with school consent. Class wellbeing monitoring and teacher dashboard. No devices required.",
        tag: "Named students · no devices",
        tagTone: "warning",
        summary: "School Classroom selected",
    },
];

/** Final step — what the practitioner does next. */
export const NEXT_ACTIONS: NextActionOption[] = [
    {
        key: "assessment",
        title: "Run the individual assessment",
        body: "Start the Session 1 baseline conversation with your participant. Their responses will shape the programme.",
    },
    {
        key: "prepare-session",
        title: "Prepare Session 1",
        body: "Review the first story and your discussion guide before delivery.",
    },
    {
        key: "rehearse",
        title: "Rehearse a scenario",
        body: "Practice the session with AI-simulated participant responses.",
    },
    {
        key: "dashboard",
        title: "Go to dashboard",
        body: "Your new programme will appear in your sessions list.",
    },
];

/**
 * The collection shown on step 3 and on the standalone overview preview.
 *
 * The "...." placeholders are the designer's, kept verbatim: they mark the copy Alison still
 * owes us, and blanking them would hide that the content is outstanding.
 */
export const MOCK_COLLECTION: CollectionOverview = {
    id: 1,
    name: "A Real Durwan - update",
    storyCount: 6,
    ageRange: "18 Years",
    sectoralApplications: ["Primary school", "Youth group"],
    curriculumKeywords: ["Caring", "Confidence building", "Self esteem", "Caring for others"],
    curriculumOverview:
        "This curriculum focuses on improving the mental health and emotional wellbeing of young people, looking at topics like identity, self-image, friendships, stress, change and help-seeking...",
    sessionCount: 6,
    learningTransfer: "At the end of the 6 sessions participants will have learned to .....",
    impact: "....",
    stories: [
        {
            position: 1,
            title: "A Real Durwan",
            theme: "Caring for others",
            learningObjective:
                "This story invites the young people to apply the theme of “Caring for others” allowing them to explore identity with curiosity rather than certainty...",
            learningTransfer: "....",
        },
        { position: 2, title: "....", theme: "....", learningObjective: "....", learningTransfer: "...." },
        { position: 3, title: "....", theme: "....", learningObjective: "....", learningTransfer: "...." },
        { position: 4, title: "....", theme: "....", learningObjective: "....", learningTransfer: "...." },
        { position: 5, title: "....", theme: "....", learningObjective: "....", learningTransfer: "...." },
        { position: 6, title: "....", theme: "....", learningObjective: "....", learningTransfer: "...." },
    ],
};

/** Collection name shown in the summary rail — the canvas uses a different one from step 3. */
export const SUMMARY_COLLECTION_NAME = "Stories for Little Navigators";

/** Assessment schedule sentence on the ready step. */
export const SESSION_SCHEDULE_SUMMARY = "6 sessions · Assessment at S1 and S6";

/** Where the stepper starts. Matches the canvas's initial state. */
export const INITIAL_DRAFT: ProgrammeDraft = {
    population: "child",
    deliveryFormat: "one-to-one",
    collectionId: MOCK_COLLECTION.id,
    collectionSelected: false,
    name: "",
    startDate: "",
    frequency: "",
    contextNotes: "",
    caseNoteFileName: "",
    nextAction: "assessment",
};
